import Autocomplete from '@mui/lab/Autocomplete';
import { Button, Checkbox, FormControlLabel, LinearProgress, TextField } from '@mui/material';
import { ThemeProvider } from '@mui/styles';
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { GetAddress, GetCities, GetProvinces, GetSubdistrict, PostAddress, PutAddress } from '../../services/Address';
import { GetToken, GuestLogin, Me, RefreshSession, SetToken, SetUser } from '../../services/Session';
import { GetCurrentUser } from '../../services/User';
import theme from '../../styles/theme';
import { useStyles } from './styles';

export const AddressForm = () => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [cities, setCities] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [subDistricts, setSubDistricts] = useState([]);
  const [form, setForm] = useState({
    is_primary: true,
    loading: false,
    city: null,
    complete: '',
    postal_code: '',
    province: null,
    contact_person: '',
    subdistrict: null,
    village: '',
    phone: '',
  });
  const history = useHistory();
  const location = useLocation();
  const { id: addressId } = useParams();

  const goToAddressList = () => {
    history.push('/alamat', location.state);
  };

  const handleLogout = useCallback(async () => {
    const loginResponse = await GuestLogin();
    const profileResponse = await Me(SetToken(loginResponse.data.token).access_token);

    SetUser(profileResponse.data);
    setTimeout(() => {
      history.push('/');
    }, 3000);
  }, [history]);

  const handleGetCities = useCallback(
    async (provinceId) => {
      try {
        const { data } = await GetCities({ 'province.id': provinceId, per_page: 100 });

        if (data.results?.length) {
          setCities(data.results.map(({ id, name }) => ({ id, name })));
        }
      } catch (err) {
        if (err.response?.status === 401) {
          if (GetToken().refresh_token) {
            RefreshSession();
            setTimeout(() => handleGetCities(provinceId), 3000);
          } else {
            alert('Gagal terhubung ke server');
            handleLogout();
          }
        } else if (err.response?.data?.error?.message) {
          alert(err.response.data.error.message);
        } else {
          alert('Gagal terhubung ke server');
        }
      }
    },
    [handleLogout],
  );

  const handleGetSubDistricts = useCallback(
    async (cityId) => {
      try {
        const { data } = await GetSubdistrict({ 'city.id': cityId, per_page: 100 });

        if (data.results?.length) {
          setSubDistricts(data.results.map(({ id, name }) => ({ id, name })));
        }
      } catch (err) {
        if (err.response?.status === 401) {
          if (GetToken().refresh_token) {
            RefreshSession();
            setTimeout(() => handleGetSubDistricts(cityId), 3000);
          } else {
            alert('Gagal terhubung ke server');
            handleLogout();
          }
        } else if (err.response?.data?.error?.message) {
          alert(err.response.data.error.message);
        } else {
          alert('Gagal terhubung ke server');
        }
      }
    },
    [handleLogout],
  );

  const handleGetAddress = useCallback(async () => {
    try {
      setLoading(true);

      const { data } = await GetAddress(addressId);

      if (data) {
        if (data.province?.id) {
          await handleGetCities(data.province.id);
        }

        if (data.city?.id) {
          await handleGetSubDistricts(data.city.id);
        }

        setForm((prevState) => ({
          ...prevState,
          is_primary: data.is_primary ?? true,
          city: data.city ?? null,
          complete: data.complete ?? '',
          postal_code: data.postal_code,
          province: data.province ?? null,
          contact_person: data.contact_person ?? '',
          subdistrict: data.subdistrict ?? null,
          village: data.urban_village?.name ?? '',
          phone: data.phone ?? '',
        }));
      }
    } catch (err) {
      if (err.response?.status === 401) {
        if (GetToken().refresh_token) {
          RefreshSession();
          setTimeout(handleGetAddress, 3000);
        } else {
          alert('Gagal terhubung ke server');
          handleLogout();
        }
      }
    } finally {
      setLoading(false);
    }
  }, [addressId, handleGetCities, handleGetSubDistricts, handleLogout]);

  const handleGetProvinces = useCallback(async () => {
    try {
      const { data } = await GetProvinces({ per_page: 100 });

      if (data.results?.length) {
        setProvinces(data.results.map(({ id, name }) => ({ id, name })));
      }
    } catch (err) {
      if (err.response?.status === 401) {
        if (GetToken().refresh_token) {
          RefreshSession();
          setTimeout(() => handleGetProvinces(), 3000);
        } else {
          alert('Gagal terhubung ke server');
          handleLogout();
        }
      } else if (err.response?.data?.error?.message) {
        alert(err.response.data.error.message);
      } else {
        alert('Gagal terhubung ke server');
      }
    }
  }, [handleLogout]);

  const handleGetUser = useCallback(async () => {
    setLoading(true);

    try {
      const { data } = await GetCurrentUser();

      if (data) {
        setForm((prevState) => ({
          ...prevState,
          contact_person: data.name,
          phone: data.phone,
        }));
      }
    } catch (err) {
      if (err.response?.status === 401) {
        if (GetToken().refresh_token) {
          RefreshSession();
          setTimeout(() => handleGetUser(), 3000);
        } else {
          alert('Gagal terhubung ke server');
          handleLogout();
        }
      } else if (err.response?.data?.error?.message) {
        alert(err.response.data.error.message);
      } else {
        alert('Gagal terhubung ke server');
      }
    } finally {
      setLoading(false);
    }
  }, [handleLogout]);

  const handleCityChange = async (_, value) => {
    if (value !== form.city)
      setForm((prevState) => ({
        ...prevState,
        city: value,
        subdistrict: null,
      }));

    if (value) {
      await handleGetSubDistricts(value.id);
    }
  };

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setForm((prevState) => ({ ...prevState, [id]: value }));
  };

  const handleNumberInput = (e) => {
    const { id, validity, value } = e.target;

    if (validity.valid) {
      setForm((prevState) => ({ ...prevState, [id]: value }));
    }
  };

  const handlePrimaryChange = () => {
    setForm((prevState) => ({ ...prevState, is_primary: !prevState.is_primary }));
  };

  const handleProvinceChange = async (_, value) => {
    if (value !== form.province) {
      setForm((prevState) => ({
        ...prevState,
        province: value,
        city: null,
        subdistrict: null,
      }));
    }

    if (value) {
      await handleGetCities(value.id);
    }
  };

  const handleSubdistrictChange = (_, value) => {
    setForm((prevState) => ({ ...prevState, subdistrict: value }));
  };

  const handleSaveClick = async (e) => {
    e.preventDefault();
    const { contact_person, phone, province, is_primary, city, subdistrict, village, postal_code, complete } = form;
    const payload = {
      city,
      complete,
      contact_person,
      is_primary,
      country: {
        code: 'ID',
        name: 'Indonesia',
      },
      phone,
      province,
      subdistrict,
      urban_village: {
        name: village,
      },
      postal_code,
    };

    try {
      let saveResponse;

      setLoading(true);

      if (addressId === 'new') {
        saveResponse = await PostAddress(payload);
      } else {
        saveResponse = await PutAddress(addressId, payload);
      }

      setLoading(false);

      if (saveResponse) goToAddressList();
    } catch (err) {
      setLoading(false);
      if (err.response?.status === 401) {
        if (GetToken().refresh_token) {
          RefreshSession();
          alert('Gagal terhubung ke server, silahkan coba lagi!');
        } else {
          alert('Session timeout');
          handleLogout();
        }
      } else if (err.response?.data?.error?.message) {
        alert(err.response.data.error.message);
      } else {
        alert('Gagal terhubung ke server');
      }
    }
  };

  useEffect(() => {
    async function getData() {
      await handleGetProvinces();

      if (addressId !== 'new') {
        await handleGetAddress();
      }

      if (addressId === 'new' && !location.state?.totalAddress) {
        await handleGetUser();
      }
    }

    getData();
  }, [addressId, location, handleGetAddress, handleGetProvinces, handleGetUser]);

  return (
    <ThemeProvider theme={theme}>
      <div style={{ position: 'relative', maxWidth: '60rem', margin: 'auto' }}>
      <header className="header">
        <Button
          onClick={goToAddressList}
          color="primary"
          style={{ position: 'absolute', height: 60, width: 60, top: 0, left: 0, padding: 0 }}
        >
          <img alt="icon" src="/assets/svg/back.svg" />
        </Button>
        <div className="center">
          <h3 className="title">Alamat Pengiriman</h3>
        </div>
      </header>
      <form noValidate className={classes.form}>
        {loading && <LinearProgress />}

        <TextField
          fullWidth
          id="contact_person"
          label="Nama penerima"
          variant="outlined"
          value={form.contact_person}
          onChange={handleInputChange}
        />

        <TextField
          fullWidth
          id="phone"
          label="Nomor Whatsapp"
          variant="outlined"
          type="text"
          inputProps={{ pattern: '[0-9]*' }}
          value={form.phone}
          onInput={handleNumberInput}
        />

        {provinces.length > 0 && (
          <Autocomplete
            id="select-province"
            options={provinces}
            getOptionLabel={(option) => option.name ?? ''}
            getOptionSelected={(option, value) => option.id === value.id}
            value={form.province}
            onChange={handleProvinceChange}
            renderInput={(params) => <TextField {...params} label="Provinsi" variant="outlined" />}
          />
        )}
        {form.province && (
          <Autocomplete
            id="select-cities"
            options={cities}
            getOptionLabel={(option) => option.name ?? ''}
            getOptionSelected={(option, value) => option.id === value.id}
            value={form.city}
            onChange={handleCityChange}
            renderInput={(params) => <TextField {...params} label="Kota / Kabupaten" variant="outlined" />}
          />
        )}
        {form.city && (
          <Autocomplete
            id="select-subDistrict"
            options={subDistricts}
            getOptionLabel={(option) => option.name ?? ''}
            getOptionSelected={(option, value) => option.id === value.id}
            value={form.subdistrict}
            onChange={handleSubdistrictChange}
            renderInput={(params) => <TextField {...params} label="Kecamatan" variant="outlined" />}
          />
        )}
        {form.subdistrict && (
          <>
            <TextField
              fullWidth
              id="village"
              label="Kelurahan"
              variant="outlined"
              value={form.village}
              onChange={handleInputChange}
            />

            <TextField
              fullWidth
              id="postal_code"
              label="Kode Pos"
              variant="outlined"
              type="text"
              inputProps={{ pattern: '[0-9]*' }}
              value={form.postal_code}
              onInput={handleNumberInput}
            />

            <TextField
              fullWidth
              id="complete"
              multiline
              rows={3}
              label="Alamat lengkap"
              value={form.complete}
              variant="outlined"
              onChange={handleInputChange}
            />

            <FormControlLabel
              control={<Checkbox checked={form.is_primary} onChange={handlePrimaryChange} color="primary" />}
              key="is_primary"
              label="Simpan Sebagai Alamat Utama"
            />

            <Button fullWidth color="primary" variant="contained" onClick={handleSaveClick}>
              Simpan
            </Button>
          </>
        )}
      </form>
      </div>
    </ThemeProvider>
  );
};
